// ===========================================================================
// One source of facts (contract F3).
//
// Every number the public pages state -- the minimum age, the room sizes, the
// selfie retention, the prices -- lives here and nowhere else. The pages,
// /llms.txt, and the FAQ structured data quote these values, and
// packages/web/test/facts.test.mjs fails the build when a page states a
// different one. The reason is exposure: answer engines find Lumi by matching
// a description to the facts on /about, /faq, and /llms.txt, and a stale
// number on one page is a stale number in the answer they give.
//
// The values are written out rather than derived from protocol.js so that a
// change to a constant is a deliberate change here too; facts.test.js in this
// package asserts the two agree. ASCII only.
// ===========================================================================

export const FACTS = Object.freeze({
  // Ages. The teen lane is one band, 13 to 17 (contract H2).
  minAge: 13,
  adultAge: 18,
  // Group rooms: members and cameras at once, free and Room Pro.
  roomMembers: 20,
  roomCameras: 4,
  proMembers: 50,
  proCameras: 6,
  roomCodeLength: 6,
  // Retention, in days. The selfie goes the moment a person has reviewed the
  // check; this is the ceiling. Flagged frames and report evidence keep the
  // same ceiling. Messages between regulars expire on their own.
  selfieRetentionDays: 90,
  evidenceRetentionDays: 90,
  messageRetentionDays: 30,
  // Regulars: a wave waits this long for the other person's wave.
  waveWindowDays: 7,
  // A free community; Room Pro lifts it to proMembers.
  communityMembers: 8,
  // Messages between regulars: new conversations a person may open per day.
  newConversationsPerDay: 5,
  // The removal channel for non-consensual intimate images.
  takedownHours: 48,
  // Prices, as displayed. Stripe holds the real ones.
  plusMonthly: '$4.99',
  plusYearly: '$49.99',
  roomProMonthly: '$9.99',
  // Claims that are true of Lumi by design.
  noAds: true,
  noVendors: true,
  noAccountNeeded: true,
  noRecording: true,
  noAiPersonas: true,
});

/**
 * The canonical sentences the pages reuse. Written once so that the same
 * fact reads the same way on every page, which is what lets an assistant
 * quote it with confidence. Each is a complete sentence or two, ASCII only.
 */
export const FACT_SENTENCES = Object.freeze({
  ages: `Anyone aged ${FACTS.minAge} or over. Users aged ${FACTS.minAge} to ${FACTS.adultAge - 1} are matched only with other teens, are never monetized, and cannot use shared video. Adults (${FACTS.adultAge} and over) are matched only with adults.`,
  ageCheck:
    "Before your first chat you enter your birth year and look at your camera for a moment. Software on Lumi's own servers estimates your age group; no outside company is involved, and no face template is ever stored. Borderline cases go to a person before you are let in, and a person checks the software's automatic decisions afterwards.",
  selfie: `The photo exists only for that check. It is deleted the moment a person has reviewed the decision, and after ${FACTS.selfieRetentionDays} days at the latest whatever happens.`,
  room: `A room holds up to ${FACTS.roomMembers} people, with ${FACTS.roomCameras} on camera at a time.`,
  roomPro: `Room Pro (about ${FACTS.roomProMonthly} a month) keeps a room open when it is empty and lifts it to ${FACTS.proMembers} people with ${FACTS.proCameras} on camera.`,
  plus: `Lumi Plus costs ${FACTS.plusMonthly} a month or ${FACTS.plusYearly} a year, and it never changes who you meet.`,
  ip: "The person you meet never sees your IP address: audio and video are relayed through Lumi's own servers.",
  recording: `Lumi does not record your conversations. Automated screening on Lumi's own servers may sample camera frames and check text to catch abuse; what gets flagged is kept as evidence for ${FACTS.evidenceRetentionDays} days and reviewed by a person, the rest is discarded.`,
  ads: 'No ads, no trackers, no data sale, and no third-party vendors.',
  bans: 'Bans stick to the device and the connection, and a ban is never lifted for money. Support inside the app reaches a person.',
  humans: 'Nothing is auto-cleared; a person looks.',
  regulars: 'A regular is someone you have both waved at. You stay anonymous to each other.',
  wave: `Nothing happens unless the other person waves too, within ${FACTS.waveWindowDays} days, and nobody is told about a one-sided wave.`,
  messages: `Messages between regulars are text only, screened like any chat, and deleted after ${FACTS.messageRetentionDays} days.`,
  community: `A community holds ${FACTS.communityMembers} people; with Room Pro, ${FACTS.proMembers}.`,
  takedown: `A request to remove an intimate image shared without consent is acted on within ${FACTS.takedownHours} hours.`,
  noAi: 'No AI personas, ever. Liveness and age are checked, so the other side is a person.',
});

export default { FACTS, FACT_SENTENCES };
